import { useState } from 'react';
import { cn } from '@/lib/utils';
import { COUNTRIES, Country, formatPhone } from '@/lib/phone';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

interface PhoneInputProps {
  id?: string;
  value: string;
  country: Country;
  onValueChange: (formatted: string) => void;
  onCountryChange?: (c: Country) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

export default function PhoneInput({
  id,
  value,
  country,
  onValueChange,
  onCountryChange,
  placeholder,
  autoFocus,
  disabled,
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const maxLength = country.nationalLength + (country.formatGroups.length - 1);
  const selectorClass =
    'flex items-center gap-1.5 px-3 py-3 border-r border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-surface-dark';

  return (
    <div className="mt-1 flex rounded-lg shadow-sm border border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-primary focus-within:border-primary overflow-hidden transition-colors">
      {COUNTRIES.length >= 2 ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(selectorClass, 'hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer')}
            >
              <span>{country.flag}</span>
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{country.dialCode}</span>
              <span className="material-icons text-gray-400 text-sm">expand_more</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-1">
            {COUNTRIES.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => {
                  onCountryChange?.(c);
                  setOpen(false);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <span>{c.flag}</span>
                <span className="flex-1 text-gray-800 dark:text-gray-200">{c.name}</span>
                <span className="text-gray-500 dark:text-gray-400">{c.dialCode}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>
      ) : (
        <div className={cn(selectorClass, 'select-none')}>
          <span>{country.flag}</span>
          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{country.dialCode}</span>
        </div>
      )}
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={value}
        onChange={(e) => onValueChange(formatPhone(e.target.value, country))}
        placeholder={placeholder}
        maxLength={maxLength}
        autoFocus={autoFocus}
        disabled={disabled}
        className="flex-1 py-3 px-3 border-0 focus:outline-none focus:ring-0 text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 bg-transparent"
      />
    </div>
  );
}
